import { memo } from 'react';
import { useDroppable } from '@dnd-kit/core';
import { SortableContext, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { Plus } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { ScrollArea } from '@/components/ui/scroll-area';
import { SortableTaskCard } from './sortable-task-card';
import { TaskCard } from './task-card';
import { useUIStore } from '@/stores/ui-store';
import type { Task, TaskStatus } from '@/types';
import { cn } from '@/lib/utils';

interface KanbanColumnProps {
  status: TaskStatus;
  tasks: Task[];
  canCreateTask?: boolean;
  canDragTasks?: boolean;
}

export const KanbanColumn = memo(function KanbanColumn({
  status,
  tasks,
  canCreateTask = true,
  canDragTasks = true,
}: KanbanColumnProps) {
  const { openCreateTask } = useUIStore();

  const { setNodeRef, isOver } = useDroppable({
    id: `column-${status.id}`,
    disabled: !canDragTasks,
  });

  const renderTasks = () => {
    if (canDragTasks) {
      return (
        <SortableContext items={tasks.map((t) => t.id)} strategy={verticalListSortingStrategy}>
          <div className="space-y-2">
            {tasks.map((task) => (
              <SortableTaskCard key={task.id} task={task} />
            ))}
          </div>
        </SortableContext>
      );
    }

    return (
      <div className="space-y-2">
        {tasks.map((task) => (
          <TaskCard key={task.id} task={task} />
        ))}
      </div>
    );
  };

  return (
    <div
      className={cn(
        'flex w-72 flex-shrink-0 flex-col rounded-lg bg-secondary/50',
        canDragTasks && isOver && 'ring-2 ring-primary ring-offset-2 ring-offset-background'
      )}
    >
      <div className="flex items-center justify-between p-3">
        <div className="flex items-center gap-2">
          <div className="h-3 w-3 rounded-full" style={{ backgroundColor: status.color }} />
          <h3 className="font-medium">{status.name}</h3>
          <span className="rounded-full bg-muted px-2 py-0.5 text-xs text-muted-foreground">
            {tasks.length}
          </span>
        </div>
      </div>

      <ScrollArea className="flex-1 px-2">
        <div ref={setNodeRef} className="min-h-[100px] pb-2">
          {renderTasks()}
        </div>
      </ScrollArea>

      {canCreateTask && (
        <div className="p-2">
          <Button
            variant="ghost"
            className="w-full justify-start gap-2 text-muted-foreground"
            onClick={() => openCreateTask(status.id)}
          >
            <Plus className="h-4 w-4" />
            Add task
          </Button>
        </div>
      )}
    </div>
  );
});
